import { AspectRatio, Box, Button } from "@mui/joy";
import { useRef, useState } from "react";
import { theme } from "./Theme";
import {
  PlayIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
} from "@heroicons/react/24/solid";

type ResponsiveDimension =
  | number
  | string
  | {
      xs?: number | string;
      sm?: number | string;
      md?: number | string;
      lg?: number | string;
      xl?: number | string;
    };

type Props = {
  aspectRatio?: string;
  width: ResponsiveDimension;
  height: ResponsiveDimension;
  videoSrc: string;
  className?: string;
  disabled?: boolean;
  playButtonConfig?: {
    width?: number | string;
    height?: number | string;
    icon?: {
      width?: number | string;
      height?: number | string;
    };
  };
  stopProp?: boolean;
  withMute?: boolean;
  ratioFill?: "solid" | "soft" | "plain";
};

const VideoPlayer = ({
  aspectRatio = "1",
  width,
  height,
  videoSrc,
  disabled = false,
  playButtonConfig = {
    width: "80px",
    height: "80px",
    icon: {
      width: 40,
      height: 40,
    },
  },
  stopProp = false,
  withMute = false,
  ratioFill = "solid",
}: Props) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);

  const handleToggleVideo = (e: any) => {
    const shouldPreventPlay = e.currentTarget.tagName === "VIDEO" && stopProp;
    const video = videoRef.current;
    if (!video || disabled) return;

    try {
      if (video.paused) {
        // prevent opening modal when video played
        if (shouldPreventPlay) {
          return;
        }

        video
          .play()
          .then(() => setIsPlaying(true))
          .catch((error) => {
            console.error("Playback failed:", error);
          });
      } else {
        if (stopProp) {
          e.stopPropagation();
        }

        video.pause();
        video.currentTime = 0;
        setIsPlaying(false);
      }
    } catch (error) {
      console.error("Video error:", error);
    }
  };

  const handleVideoEnd = () => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = 0;
    setIsPlaying(false);
  };

  const handleToggleMute = (e: any) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  return (
    <Box
      sx={{
        position: "relative",
      }}
    >
      <AspectRatio
        ratio={aspectRatio}
        variant={ratioFill}
        sx={{
          width: width,
          height: height,
          borderRadius: "8px",
          objectFit: "cover",
        }}
      >
        <video
          ref={videoRef}
          src={videoSrc}
          width={"100%"}
          height={"100%"}
          style={{
            objectFit: "contain",
            cursor: "pointer",
          }}
          onClick={(e) => handleToggleVideo(e)}
          onEnded={handleVideoEnd}
        />
      </AspectRatio>

      {/* play button (video only) */}
      {!isPlaying && (
        <Button
          onClick={(e) => {
            if (stopProp) {
              e.stopPropagation();
            }

            handleToggleVideo(e);
          }}
          variant="plain"
          sx={{
            position: "absolute",
            borderRadius: "100%",
            width: playButtonConfig?.width,
            minWidth: playButtonConfig?.width,
            height: playButtonConfig?.height,
            backgroundColor: theme.palette.others.video_playbutton_bg,
            opacity: "72%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            top: "50%",
            left: "50%",
            p: 0,
            transform: "translateY(-50%) translateX(-50%)",
            "&:hover .hover-icon": {
              color: theme.palette.common.black,
            },
          }}
        >
          <PlayIcon
            width={playButtonConfig?.icon?.width}
            height={playButtonConfig?.icon?.height}
            style={{
              opacity: 100,
              color: theme.palette.common.white,
            }}
            className="hover-icon"
          />
        </Button>
      )}

      {/* mute button */}
      {withMute && isPlaying && (
        <Button
          variant="plain"
          onClick={handleToggleMute}
          sx={{
            position: "absolute",
            width: "32px",
            minWidth: "32px",
            height: "24px",
            minHeight: "24px",
            right: "16px",
            bottom: "16px",
            backgroundColor: theme.palette.others.carousel_index_bg,
            opacity: 50,
            px: "8px",
            py: "4px",
          }}
        >
          {isMuted ? (
            <SpeakerXMarkIcon width={12} height={10.67} />
          ) : (
            <SpeakerWaveIcon width={12} height={10.67} />
          )}
        </Button>
      )}
    </Box>
  );
};

export default VideoPlayer;
